import React, { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import useForm from "../../../hooks/useForm";
import Input from "../../form/front/Input";
import FileUploadSingle from "../../form/FileUploadSingle";
import Button from "../../form/front/Button";
import { profileUpdate } from "../../../services/ProfileService";
import Textarea from "../../form/front/Textarea";
import { useDispatch } from "react-redux";
import { setProfile } from "../../../redux/slice/profileSlice";

const validation = {
  firstName: { required: true, message: "First name required" },
  email: [
    { required: true, message: "Email required" },
    {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email",
    },
  ],
};

const ManageProfile = ({ user, close }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({ validationRules: validation });

  // ✅ Fill form initially
  useEffect(() => {
    if (user) {
      setFieldsValue({
        _id: user?._id,
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        bio: user?.bio || "",
        role: user?.role || "",
        image: user?.image?.url || "",
        coverImage: user?.coverMedia?.url || "",
      });
    }
  }, [user]);

  // ✅ Mutation with cache update
  const { mutate, isPending } = useMutation({
    mutationFn: async (formValues) => {
      const formData = new FormData();

      formData.append("userId", user?._id);
      formData.append("firstName", formValues.firstName);
      formData.append("lastName", formValues.lastName);
      formData.append("bio", formValues.bio || "");
      if (formValues.image && typeof formValues.image === "object") {
        formData.append("image", formValues.image);
      }
      if (formValues.coverImage && typeof formValues.coverImage === "object") {
        formData.append("coverMedia", formValues.coverImage);
      }

      return await profileUpdate(formData);
    },

    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Profile updated");

        // 🔥 UPDATE UI WITHOUT REFRESH
        // queryClient.setQueryData(["profile-details"], (old) => {
        //   return {
        //     ...old,
        //     data: {
        //       ...old?.data,
        //       ...res?.data,
        //     },
        //   };
        // });
        queryClient.invalidateQueries(["profile-details"]);
        dispatch(setProfile(res?.data));
        close();
      } else {
        toast.error(res?.message);
      }
    },
  });

  return (
    <form onSubmit={(e) => handleSubmit(e, mutate)}>
      <div className="space-y-6">
        {/* Name */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            inputName="firstName"
            value={values.firstName}
            onChange={handleChange}
            {...formValidate(errors, "firstName")}
          />

          <Input
            label="Last Name"
            inputName="lastName"
            value={values.lastName}
            onChange={handleChange}
          />
        </div>

        {/* Email */}
        <Input
          label="Email"
          inputName="email"
          value={values.email}
          disabled
          {...formValidate(errors, "email")}
        />
        <Textarea
          label="Bio"
          inputName="bio"
          inputValue={values.bio || ""}
          onChange={handleChange}
        />

        <div className="grid grid-cols-2 gap-6">
          {/* Profile Image */}
          {/* <FileUploadSingle
            label="Profile Image"
            value={values.image}
            onChange={(file) =>
              handleChange({
                target: { name: "image", value: file },
              })
            }
          /> */}

          {/* 🔥 NEW COVER IMAGE */}
          {/* <FileUploadSingle
            label="Cover Image"
            value={values.coverImage}
            onChange={(file) =>
              handleChange({
                target: { name: "coverImage", value: file },
              })
            }
          /> */}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            buttonLabel="Cancel"
            buttonFunction={close}
            buttonClasses="border px-4 h-9 rounded-full"
          />
          <Button
            buttonType="submit"
            buttonLabel="Save"
            loading={isPending}
            buttonClasses="bg-blue-600 text-white px-4 h-9 rounded-full"
          />
        </div>
      </div>
    </form>
  );
};

export default ManageProfile;
